// Standalone practice/warmup metronome. Deliberately independent of the
// Performance/Setlist system: it has its own AudioContext and its own
// MetronomeEngine instance, so opening this tab never touches setlist state
// and can't conflict with a song loaded in Performance mode.
//
// Output routing matches the setlist metronome: right-channel only, via the
// same explicit splitter/merger graph, so behavior is consistent regardless
// of whether the tablet happens to still be wired into the stereo breakout
// cable between songs or sets.

import '../playerTransportUtilities.css';
import '../metronomeSettingsSheet.css';
import { MetronomeEngine, TapTempo, type BeatEvent } from '../audio/MetronomeEngine';
import { CLICK_SOUND_LABELS, type ClickSound } from '../audio/clickSounds';
import { DEFAULT_METRONOME_SETTINGS, type MetronomeSettings } from '../core/types';
import { secondsPerBeat } from '../core/markerFormat';
import { metronomePresetStore } from '../core/metronomePresetStore';
import { promptDialog, confirmDialog } from '../ui/confirm';
import { showToast } from '../ui/toast';
import { ICON_PLAY, ICON_PAUSE } from '../ui/transportIcons';
import { renderBeatVisual, scheduleBeatPulse, type BeatVisualEls } from '../ui/beatVisual';
import { resumeAudioContextOnVisible } from '../core/audioContextRecovery';

const SOUND_OPTIONS: ClickSound[] = ['beep', 'woodblock', 'click', 'cowbell'];
const TIME_SIGNATURES: { label: string; beatsPerMeasure: number; beatUnit: 4 | 8 }[] = [
  { label: '2/4', beatsPerMeasure: 2, beatUnit: 4 },
  { label: '3/4', beatsPerMeasure: 3, beatUnit: 4 },
  { label: '4/4', beatsPerMeasure: 4, beatUnit: 4 },
  { label: '5/4', beatsPerMeasure: 5, beatUnit: 4 },
  { label: '6/8', beatsPerMeasure: 6, beatUnit: 8 },
  { label: '7/8', beatsPerMeasure: 7, beatUnit: 8 },
];

let sharedAudioContext: AudioContext | null = null;

function getAudioContext(): AudioContext {
  if (!sharedAudioContext) sharedAudioContext = new AudioContext();
  return sharedAudioContext;
}

resumeAudioContextOnVisible(() => sharedAudioContext);

export function renderMetronomeSection(container: HTMLElement): () => void {
  container.innerHTML = '';

  let settings: MetronomeSettings = { ...DEFAULT_METRONOME_SETTINGS };
  let engine: MetronomeEngine | null = null;
  let isRunning = false;
  const tapTempo = new TapTempo();
  let beatVisualEls: BeatVisualEls = { pills: [], labels: [] };
  let settingsHideTimer: number | null = null;

  const wrapper = document.createElement('div');
  wrapper.className = 'performance metronome-page';
  wrapper.innerHTML = `
    <div class="performance-grid">
      <div class="performance-col now-playing">
        <div class="metronome-hero-card">
          <h1 class="current-song-title" id="bpm-display">120 BPM</h1>

          <div class="progress-wrap">
            <input type="range" id="bpm-slider" class="bpm-range" min="30" max="300" value="120" />
            <div class="time-row">
              <button class="btn btn-small" id="bpm-minus">-1 BPM</button>
              <button class="btn btn-small" id="tap-tempo-btn">TAP TEMPO</button>
              <button class="btn btn-small" id="bpm-plus">+1 BPM</button>
            </div>
          </div>

          <div class="visual-row">
            <div class="metronome-visual" id="beat-dots"></div>
          </div>

          <button class="metronome-toggle-btn" id="metro-toggle-btn" type="button" aria-pressed="false">
            <span class="btn-transport-icon" id="metro-toggle-icon">${ICON_PLAY}</span>
            <span class="metronome-toggle-label" id="metro-toggle-label">PLAY</span>
          </button>
        </div>

        <div class="metronome-bottom-bar">
          <button class="metronome-settings-fab" id="metronome-settings-fab" type="button" title="Settings" aria-label="Open settings" aria-expanded="false" aria-controls="metronome-settings-sheet">
            <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><path d="M12 8.5a3.5 3.5 0 1 0 0 7 3.5 3.5 0 0 0 0-7Z" fill="none" stroke="currentColor" stroke-width="2"/><path d="m19.4 15 .1.1 1.5 1.2-2 3.4-1.8-.7-.2-.1a8 8 0 0 1-2.2 1.3l-.1.2-.3 2.2h-4l-.3-2.2-.1-.2a8 8 0 0 1-2.2-1.3l-.2.1-1.8.7-2-3.4 1.5-1.2.1-.1a8 8 0 0 1 0-2.6l-.1-.1L3.5 11l2-3.4 1.8.7.2.1A8 8 0 0 1 9.7 7l.1-.2.3-2.2h4l.3 2.2.1.2a8 8 0 0 1 2.2 1.3l.2-.1 1.8-.7 2 3.4-1.5 1.2-.1.1a8 8 0 0 1 0 2.6Z" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </button>
        </div>
      </div>

      <div class="player-utility-drawer metronome-settings-drawer" id="metronome-settings-sheet" hidden>
        <button type="button" class="player-utility-drawer-scrim metronome-settings-scrim" id="metronome-settings-scrim" aria-label="Close metronome settings"></button>
        <section class="player-utility-drawer-sheet metronome-settings-sheet" role="dialog" aria-modal="true" aria-labelledby="metronome-settings-title">
          <div class="player-utility-drawer-handle" aria-hidden="true"></div>
          <div class="player-utility-drawer-heading">
            <div>
              <h2 id="metronome-settings-title">Metronome settings</h2>
              <p class="metronome-settings-subtitle">Sound, feel, count-in and presets</p>
            </div>
            <button class="player-utility-drawer-close" id="metronome-settings-close" type="button" aria-label="Close settings">&times;</button>
          </div>

          <div class="player-utility-drawer-options metronome-settings-body">
            <section class="metronome-settings-section" aria-labelledby="metro-timing-heading">
              <h3 id="metro-timing-heading">Timing</h3>
              <div class="metronome-settings-select-row">
                <label class="metronome-setting-field">Time signature<select id="time-sig-select"></select></label>
                <label class="metronome-setting-field">Subdivision<select id="subdivision-select">
                  <option value="1">Quarter notes</option>
                  <option value="2">Eighth notes</option>
                  <option value="4">Sixteenth notes</option>
                </select></label>
              </div>
              <label class="metronome-toggle-row"><span>Accent first beat</span><input type="checkbox" id="accent-toggle" /></label>
            </section>

            <section class="metronome-settings-section" aria-labelledby="metro-sound-heading">
              <h3 id="metro-sound-heading">Sound</h3>
              <div class="metronome-choice-grid" id="sound-picker"></div>
              <label class="metronome-slider-field" for="volume-slider"><span>Click volume</span><input type="range" id="volume-slider" min="0" max="1" step="0.05" value="0.8" /></label>
              <div class="metronome-setting-stack">
                <span class="metronome-setting-label">Output channel</span>
                <div class="metronome-choice-grid metronome-choice-grid-compact" id="channel-picker"></div>
              </div>
            </section>

            <section class="metronome-settings-section" aria-labelledby="metro-count-heading">
              <h3 id="metro-count-heading">Count-in</h3>
              <div class="metronome-choice-grid metronome-choice-grid-compact" id="count-in-picker"></div>
            </section>

            <section class="metronome-settings-section" aria-labelledby="metro-presets-heading">
              <div class="metronome-settings-section-heading">
                <h3 id="metro-presets-heading">Presets</h3>
                <button class="metronome-save-preset" id="save-preset-btn">Save preset</button>
              </div>
              <div class="metronome-preset-list" id="preset-list"></div>
            </section>
          </div>
        </section>
      </div>
    </div>
  `;
  container.appendChild(wrapper);

  const bpmDisplay = wrapper.querySelector<HTMLDivElement>('#bpm-display')!;
  const bpmSlider = wrapper.querySelector<HTMLInputElement>('#bpm-slider')!;
  const bpmMinus = wrapper.querySelector<HTMLButtonElement>('#bpm-minus')!;
  const bpmPlus = wrapper.querySelector<HTMLButtonElement>('#bpm-plus')!;
  const tapTempoBtn = wrapper.querySelector<HTMLButtonElement>('#tap-tempo-btn')!;
  const beatDotsWrap = wrapper.querySelector<HTMLDivElement>('#beat-dots')!;
  const toggleBtn = wrapper.querySelector<HTMLButtonElement>('#metro-toggle-btn')!;
  const toggleIcon = wrapper.querySelector<HTMLSpanElement>('#metro-toggle-icon')!;
  const toggleLabel = wrapper.querySelector<HTMLSpanElement>('#metro-toggle-label')!;
  const timeSigSelect = wrapper.querySelector<HTMLSelectElement>('#time-sig-select')!;
  const subdivisionSelect = wrapper.querySelector<HTMLSelectElement>('#subdivision-select')!;
  const accentToggle = wrapper.querySelector<HTMLInputElement>('#accent-toggle')!;
  const soundPicker = wrapper.querySelector<HTMLDivElement>('#sound-picker')!;
  const volumeSlider = wrapper.querySelector<HTMLInputElement>('#volume-slider')!;
  const countInPicker = wrapper.querySelector<HTMLDivElement>('#count-in-picker')!;
  const channelPicker = wrapper.querySelector<HTMLDivElement>('#channel-picker')!;
  const savePresetBtn = wrapper.querySelector<HTMLButtonElement>('#save-preset-btn')!;
  const presetList = wrapper.querySelector<HTMLDivElement>('#preset-list')!;
  const settingsFab = wrapper.querySelector<HTMLButtonElement>('#metronome-settings-fab')!;
  const settingsSheet = wrapper.querySelector<HTMLDivElement>('#metronome-settings-sheet')!;
  const settingsScrim = wrapper.querySelector<HTMLButtonElement>('#metronome-settings-scrim')!;
  const settingsCloseBtn = wrapper.querySelector<HTMLButtonElement>('#metronome-settings-close')!;

  function openSettingsSheet(): void {
    if (settingsHideTimer !== null) {
      window.clearTimeout(settingsHideTimer);
      settingsHideTimer = null;
    }
    settingsSheet.hidden = false;
    settingsFab.setAttribute('aria-expanded', 'true');
    requestAnimationFrame(() => {
      settingsSheet.classList.add('is-open');
      settingsScrim.classList.add('is-visible');
      settingsCloseBtn.focus();
    });
  }

  function closeSettingsSheet(): void {
    settingsSheet.classList.remove('is-open');
    settingsScrim.classList.remove('is-visible');
    settingsFab.setAttribute('aria-expanded', 'false');
    settingsHideTimer = window.setTimeout(() => {
      settingsSheet.hidden = true;
      settingsHideTimer = null;
      settingsFab.focus();
    }, 190);
  }

  function handleSettingsKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && !settingsSheet.hidden) closeSettingsSheet();
  }

  settingsFab.addEventListener('click', openSettingsSheet);
  settingsCloseBtn.addEventListener('click', closeSettingsSheet);
  settingsScrim.addEventListener('click', closeSettingsSheet);
  document.addEventListener('keydown', handleSettingsKeydown);

  timeSigSelect.innerHTML = TIME_SIGNATURES.map(
    (sig, i) => `<option value="${i}">${sig.label}</option>`,
  ).join('');

  soundPicker.innerHTML = SOUND_OPTIONS.map(
    (sound) => `<button class="metronome-choice sound-option" data-sound="${sound}">${CLICK_SOUND_LABELS[sound]}</button>`,
  ).join('');

  countInPicker.innerHTML = [0, 1, 2, 4]
    .map((n) => `<button class="metronome-choice count-in-option" data-count-in="${n}">${n === 0 ? 'Off' : `${n} bar${n > 1 ? 's' : ''}`}</button>`)
    .join('');

  channelPicker.innerHTML = (['stereo', 'left', 'right'] as const)
    .map((ch) => `<button class="metronome-choice channel-option" data-channel="${ch}">${ch === 'stereo' ? 'Stereo' : ch === 'left' ? 'Left' : 'Right'}</button>`)
    .join('');

  function renderBeatDots(): void {
    beatVisualEls = renderBeatVisual(beatDotsWrap, settings.beatsPerMeasure, settings.accentFirstBeat);
  }

  function syncControlsFromSettings(): void {
    bpmSlider.value = String(settings.bpm);
    bpmDisplay.textContent = `${settings.bpm} BPM`;
    const min = Number(bpmSlider.min);
    const max = Number(bpmSlider.max);
    const pct = ((settings.bpm - min) / (max - min)) * 100;
    bpmSlider.style.setProperty('--bpm-fill', `${pct}%`);

    const sigIndex = TIME_SIGNATURES.findIndex(
      (s) => s.beatsPerMeasure === settings.beatsPerMeasure && s.beatUnit === settings.beatUnit,
    );
    timeSigSelect.value = String(sigIndex >= 0 ? sigIndex : 2);
    subdivisionSelect.value = String(settings.subdivision);
    accentToggle.checked = settings.accentFirstBeat;
    volumeSlider.value = String(settings.clickVolume);

    for (const btn of soundPicker.querySelectorAll<HTMLButtonElement>('.sound-option')) {
      btn.classList.toggle('is-selected', btn.dataset.sound === settings.sound);
    }
    for (const btn of countInPicker.querySelectorAll<HTMLButtonElement>('.count-in-option')) {
      btn.classList.toggle('is-selected', Number(btn.dataset.countIn) === settings.countInMeasures);
    }
    for (const btn of channelPicker.querySelectorAll<HTMLButtonElement>('.channel-option')) {
      btn.classList.toggle('is-selected', btn.dataset.channel === settings.clickChannel);
    }

    renderBeatDots();
  }

  function applySettingsChange(patch: Partial<MetronomeSettings>): void {
    settings = { ...settings, ...patch };
    engine?.updateSettings(settings);
    syncControlsFromSettings();
  }

  function renderPresets(): void {
    const presets = metronomePresetStore.getPresets();
    presetList.innerHTML = '';
    if (presets.length === 0) {
      presetList.innerHTML = '<p class="metronome-preset-empty">No saved presets yet.</p>';
      return;
    }
    for (const preset of presets) {
      const row = document.createElement('div');
      row.className = 'metronome-preset-row';
      row.innerHTML = `
        <div class="metronome-preset-copy">
          <strong>${escapeHtml(preset.name)}</strong>
          <span>${preset.settings.bpm} BPM · ${preset.settings.beatsPerMeasure}/${preset.settings.beatUnit} · ${CLICK_SOUND_LABELS[preset.settings.sound]}</span>
        </div>
        <div class="metronome-preset-actions">
          <button type="button" data-action="load-preset" data-id="${preset.id}">Load</button>
          <button type="button" data-action="rename-preset" data-id="${preset.id}">Rename</button>
          <button type="button" class="is-danger" data-action="delete-preset" data-id="${preset.id}">Delete</button>
        </div>
      `;
      presetList.appendChild(row);
    }
  }

  function ensureEngine(): MetronomeEngine {
    if (!engine) {
      engine = new MetronomeEngine(getAudioContext(), settings);
      engine.subscribe((event) => {
        if (event.type === 'beat') handleBeat(event.beat);
        if (event.type === 'stopped') {
          isRunning = false;
          updatePlayStopUi();
        }
      });
    }
    return engine;
  }

  function handleBeat(beat: BeatEvent): void {
    const secondsPerTick = secondsPerBeat(settings) / settings.subdivision;
    const isDownbeat = beat.beatIndexInMeasure === 0 && settings.accentFirstBeat;
    scheduleBeatPulse(beatVisualEls, beat, settings.subdivision, secondsPerTick, getAudioContext(), isDownbeat);
  }

  function updatePlayStopUi(): void {
    toggleBtn.classList.toggle('is-running', isRunning);
    toggleBtn.setAttribute('aria-pressed', String(isRunning));
    toggleIcon.innerHTML = isRunning ? ICON_PAUSE : ICON_PLAY;
    toggleLabel.textContent = isRunning ? 'PAUSE' : 'PLAY';
  }

  toggleBtn.addEventListener('click', async () => {
    if (isRunning) {
      engine?.stop();
      isRunning = false;
      updatePlayStopUi();
      return;
    }
    const activeEngine = ensureEngine();
    isRunning = true;
    updatePlayStopUi();
    await activeEngine.start();
  });

  bpmSlider.addEventListener('input', () => {
    applySettingsChange({ bpm: Number(bpmSlider.value) });
  });
  bpmMinus.addEventListener('click', () => {
    applySettingsChange({ bpm: Math.max(30, settings.bpm - 1) });
  });
  bpmPlus.addEventListener('click', () => {
    applySettingsChange({ bpm: Math.min(300, settings.bpm + 1) });
  });
  tapTempoBtn.addEventListener('click', () => {
    const bpm = tapTempo.tap(Date.now());
    if (bpm !== null) applySettingsChange({ bpm });
  });

  timeSigSelect.addEventListener('change', () => {
    const sig = TIME_SIGNATURES[Number(timeSigSelect.value)];
    applySettingsChange({ beatsPerMeasure: sig.beatsPerMeasure, beatUnit: sig.beatUnit });
  });
  subdivisionSelect.addEventListener('change', () => {
    applySettingsChange({ subdivision: Number(subdivisionSelect.value) as 1 | 2 | 4 });
  });
  accentToggle.addEventListener('change', () => {
    applySettingsChange({ accentFirstBeat: accentToggle.checked });
  });
  volumeSlider.addEventListener('input', () => {
    applySettingsChange({ clickVolume: Number(volumeSlider.value) });
  });

  soundPicker.addEventListener('click', (e) => {
    const target = (e.target as HTMLElement).closest<HTMLButtonElement>('.sound-option');
    if (!target?.dataset.sound) return;
    applySettingsChange({ sound: target.dataset.sound as ClickSound });
  });

  countInPicker.addEventListener('click', (e) => {
    const target = (e.target as HTMLElement).closest<HTMLButtonElement>('.count-in-option');
    if (!target?.dataset.countIn) return;
    applySettingsChange({ countInMeasures: Number(target.dataset.countIn) as 0 | 1 | 2 | 4 });
  });

  channelPicker.addEventListener('click', (e) => {
    const target = (e.target as HTMLElement).closest<HTMLButtonElement>('.channel-option');
    if (!target?.dataset.channel) return;
    applySettingsChange({ clickChannel: target.dataset.channel as 'stereo' | 'left' | 'right' });
  });

  savePresetBtn.addEventListener('click', async () => {
    const name = await promptDialog({
      title: 'Save Preset',
      initialValue: `${settings.bpm} BPM ${TIME_SIGNATURES.find((s) => s.beatsPerMeasure === settings.beatsPerMeasure && s.beatUnit === settings.beatUnit)?.label ?? ''}`,
      confirmLabel: 'Save',
    });
    if (!name) return;
    await metronomePresetStore.savePreset(name, settings);
    showToast(`Preset "${name}" saved.`, 'info');
  });

  presetList.addEventListener('click', async (e) => {
    const target = (e.target as HTMLElement).closest<HTMLElement>('[data-action]');
    const action = target?.dataset.action;
    const id = target?.dataset.id;
    if (!action || !id) return;
    const preset = metronomePresetStore.getPresets().find((p) => p.id === id);
    if (!preset) return;

    if (action === 'load-preset') {
      applySettingsChange(preset.settings);
      showToast(`Loaded "${preset.name}".`, 'info');
    } else if (action === 'rename-preset') {
      const newName = await promptDialog({ title: 'Rename Preset', initialValue: preset.name, confirmLabel: 'Rename' });
      if (newName) await metronomePresetStore.renamePreset(id, newName);
    } else if (action === 'delete-preset') {
      const confirmed = await confirmDialog({
        title: 'Delete Preset',
        message: `Delete preset "${preset.name}"?`,
        confirmLabel: 'Delete',
        danger: true,
      });
      if (confirmed) await metronomePresetStore.deletePreset(id);
    }
  });

  const unsubscribePresets = metronomePresetStore.subscribe(renderPresets);
  if (!metronomePresetStore.isLoaded()) {
    void metronomePresetStore.load();
  } else {
    renderPresets();
  }

  syncControlsFromSettings();
  updatePlayStopUi();

  return () => {
    engine?.stop();
    unsubscribePresets();
    document.removeEventListener('keydown', handleSettingsKeydown);
    if (settingsHideTimer !== null) window.clearTimeout(settingsHideTimer);
  };
}

function escapeHtml(value: string): string {
  const div = document.createElement('div');
  div.textContent = value;
  return div.innerHTML;
}
